import React, { useState } from 'react';
import styled from 'styled-components';
import Card from './Card';
import Button from './Button';
import Loading from './Loading';
import Alert from './Alert';
import { formatDate, formatRelativeTime } from '../utils/helpers';

const RecommendationsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RecommendationItem = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background-color: ${({ theme }) => theme.colors.background.card};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-left: 4px solid ${({ theme, $priority }) => 
    $priority === 'high' ? theme.colors.error.main :
    $priority === 'medium' ? theme.colors.warning.main :
    theme.colors.success.main
  };
  margin-bottom: 0.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const RecommendationTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.sizes.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RecommendationPriority = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  background-color: ${({ theme, $priority }) => 
    $priority === 'high' ? theme.colors.error.light :
    $priority === 'medium' ? theme.colors.warning.light :
    theme.colors.success.light
  };
  color: ${({ theme, $priority }) => 
    $priority === 'high' ? theme.colors.error.dark :
    $priority === 'medium' ? theme.colors.warning.dark :
    theme.colors.success.dark
  };
`;

const RecommendationDescription = styled.p`
  margin: 0.5rem 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
`;

const RecommendationMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
  font-size: ${({ theme }) => theme.typography.sizes.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const RecommendationTime = styled.span`
  display: flex;
  align-items: center;
`;

const RecommendationType = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background-color: ${({ theme, type }) => 
    type === 'irrigation' ? theme.colors.info.light :
    type === 'nutrient' ? theme.colors.success.light :
    type === 'pest' ? theme.colors.warning.light :
    theme.colors.primary.light
  };
  color: ${({ theme, type }) => 
    type === 'irrigation' ? theme.colors.info.dark :
    type === 'nutrient' ? theme.colors.success.dark :
    type === 'pest' ? theme.colors.warning.dark :
    theme.colors.primary.dark
  };
`;

const RecommendationActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const RecommendationDetails = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.colors.background.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FilterButton = styled(Button)`
  font-size: ${({ theme }) => theme.typography.sizes.sm};
  padding: 0.25rem 0.75rem;
`;

const NoRecommendations = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

/**
 * RecommendationList component for displaying AI-generated recommendations
 * @param {Object} props
 * @param {Array} props.recommendations - List of recommendations
 * @param {Array} props.zones - List of farm zones
 * @param {Function} props.onCreateTask - Function to call when creating a task from recommendation
 * @param {Function} props.onDismiss - Function to call when dismissing a recommendation
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 */
const RecommendationList = ({ 
  recommendations = [], 
  zones = [],
  onCreateTask,
  onDismiss,
  loading = false,
  error = null
}) => {
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  // Filter recommendations
  const filteredRecommendations = filter === 'all' 
    ? recommendations 
    : recommendations.filter(rec => rec.priority === filter);

  // Toggle expanded recommendation
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  // Handle create task from recommendation
  const handleCreateTask = (recommendation) => {
    if (onCreateTask) {
      onCreateTask(recommendation);
    }
  };

  // Handle dismiss recommendation
  const handleDismiss = (id) => {
    if (onDismiss) {
      onDismiss(id);
    }
  };

  // Get zone name by ID
  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    return zone ? zone.name : 'Unknown Zone';
  };

  return (
    <Card title="AI Recommendations">
      <FilterContainer>
        <FilterGroup>
          <FilterButton 
            variant={filter === 'all' ? 'primary' : 'outline'} 
            size="sm"
            onClick={() => setFilter('all')}
          >
            All
          </FilterButton>
          <FilterButton 
            variant={filter === 'high' ? 'primary' : 'outline'} 
            size="sm"
            onClick={() => setFilter('high')}
          >
            High Priority
          </FilterButton>
          <FilterButton 
            variant={filter === 'medium' ? 'primary' : 'outline'} 
            size="sm"
            onClick={() => setFilter('medium')}
          >
            Medium Priority
          </FilterButton>
          <FilterButton 
            variant={filter === 'low' ? 'primary' : 'outline'} 
            size="sm"
            onClick={() => setFilter('low')}
          >
            Low Priority
          </FilterButton>
        </FilterGroup>
      </FilterContainer>

      {loading ? (
        <Loading text="Loading recommendations..." />
      ) : error ? (
        <Alert type="error" message={error} />
      ) : filteredRecommendations.length === 0 ? (
        <NoRecommendations>
          <p>No recommendations available at this time.</p>
        </NoRecommendations>
      ) : (
        <RecommendationsContainer>
          {filteredRecommendations.map(recommendation => (
            <RecommendationItem 
              key={recommendation.id} 
              $priority={recommendation.priority}
            >
              <RecommendationHeader>
                <RecommendationTitle>
                  {recommendation.title}
                </RecommendationTitle>
                <RecommendationPriority $priority={recommendation.priority}>
                  {recommendation.priority.charAt(0).toUpperCase() + recommendation.priority.slice(1)} Priority
                </RecommendationPriority>
              </RecommendationHeader>
              
              <RecommendationDescription>
                {recommendation.description}
              </RecommendationDescription>
              
              <RecommendationMeta>
                <RecommendationTime>
                  {formatRelativeTime(recommendation.timestamp)}
                </RecommendationTime>
                <RecommendationType type={recommendation.type}>
                  {recommendation.type.charAt(0).toUpperCase() + recommendation.type.slice(1)}
                </RecommendationType>
              </RecommendationMeta>
              
              {expandedId === recommendation.id && (
                <RecommendationDetails>
                  <strong>Zone:</strong> {getZoneName(recommendation.zoneId)}<br />
                  <strong>Reasoning:</strong> {recommendation.reasoning}<br />
                  <strong>Date:</strong> {formatDate(recommendation.timestamp)}
                </RecommendationDetails>
              )}
              
              <RecommendationActions>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => toggleExpand(recommendation.id)}
                >
                  {expandedId === recommendation.id ? 'Hide Details' : 'Show Details'}
                </Button>
                <div>
                  {recommendation.actionable && (
                    <Button 
                      variant="primary" 
                      size="sm" 
                      onClick={() => handleCreateTask(recommendation)}
                      style={{ marginRight: '0.5rem' }}
                    >
                      Create Task
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDismiss(recommendation.id)}
                  >
                    Dismiss
                  </Button>
                </div>
              </RecommendationActions>
            </RecommendationItem>
          ))}
        </RecommendationsContainer>
      )}
    </Card>
  );
};

export default RecommendationList;
